import { useEffect, useState } from 'react';

// sessionStorage key that persists the embedded-mode flag across client-side
// route changes. Kept module-private so callers must go through
// `useEmbeddedMode()` or `isMobileAppSession()` — both surfaces apply the
// dev-mode tripwire that catches ordering bugs, which a raw sessionStorage
// read would bypass.
const EMBEDDED_MODE_KEY = 'embedded_mode_source';

const readInitialSource = () => {
  if (typeof window === 'undefined') return null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const fromParam = urlParams.get('from');
    if (fromParam) return fromParam;
    return window.sessionStorage.getItem(EMBEDDED_MODE_KEY);
  } catch (e) {
    // sessionStorage / URLSearchParams can throw in restricted contexts
    // (Safari private mode, sandboxed iframe). Surface it in dev so the
    // ordering bug is debuggable; production silently treats embedded
    // mode as off, which is the safe default for a chrome-hiding flag.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[useEmbeddedMode] readInitialSource threw:', e);
    }
    return null;
  }
};

// Reads the embedded-mode flag set by the mobile WebView (or other host).
// Mobile sets `?from=mobile` on the initial URL; we stash it in sessionStorage
// so client-side route changes don't lose the flag. Returns a stable shape
// even if `window`/`sessionStorage` is unavailable so consumers can always
// destructure safely.
//
// **Prefer this hook over `isMobileAppSession()` in any component-render
// context.** The hook also reads the URL `?from=` param on first mount,
// which the standalone helper cannot do — components calling this guarantee
// the sessionStorage value is populated before any non-component caller
// (mutation callback, event handler) later relies on it.
export const useEmbeddedMode = () => {
  const [source] = useState(readInitialSource);

  useEffect(() => {
    if (typeof window === 'undefined' || !source) return;
    try {
      if (window.sessionStorage.getItem(EMBEDDED_MODE_KEY) !== source) {
        window.sessionStorage.setItem(EMBEDDED_MODE_KEY, source);
      }
    } catch (_e) {
      // sessionStorage may be disabled in private modes; ignore.
    }
  }, [source]);

  return {
    isEmbedded: Boolean(source),
    source,
    isMobileApp: source === 'mobile',
  };
};

/**
 * One-shot read of the mobile-app embedded-mode flag for non-component
 * callers (e.g. inside an event handler or mutation callback where a React
 * hook can't be used). Mirrors `useEmbeddedMode().isMobileApp` exactly so
 * the comparison logic lives in a single place.
 *
 * Note on ordering: this reads sessionStorage only, NOT the URL `?from`
 * param. `useEmbeddedMode` runs on mount and stashes the URL value to
 * sessionStorage; consumers of `isMobileAppSession()` therefore assume
 * `useEmbeddedMode` has already executed at least once (i.e. App-level
 * render has happened). For everyone calling this from inside a mutation
 * callback or event handler that's guaranteed, but a module-top-level
 * initializer would see `false` even when `?from=mobile` is present.
 */
export const isMobileAppSession = () => {
  if (typeof window === 'undefined') return false;
  try {
    const stored = window.sessionStorage.getItem(EMBEDDED_MODE_KEY);
    // Dev-mode tripwire: if `?from=` is in the URL but sessionStorage hasn't
    // been populated yet, this helper is being called before
    // `useEmbeddedMode` had a chance to run (e.g. from a module
    // initializer). Surface it loudly in dev so the ordering bug is caught
    // at the call site rather than silently returning `false`.
    if (
      process.env.NODE_ENV !== 'production' &&
      stored == null &&
      new URLSearchParams(window.location.search).get('from')
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[isMobileAppSession] called before useEmbeddedMode initialised — ' +
        'returning false despite ?from= being present. Ensure App has ' +
        'rendered at least once before this read.',
      );
    }
    return stored === 'mobile';
  } catch (_e) {
    return false;
  }
};
